import React from 'react';
import { useTranslation } from 'react-i18next';
import { default as ReactSelect, components } from 'react-select';

import { classnames } from '../../../../utils/classnames'
import { autocompleteDisabled } from '../../../../utils/utils';
import { ReactComponent as Check } from '../../../../picture/Check.svg'
import InputErrors, { withErrors } from '../inputErrors/inputErrors';
import './dropdown.css';

const AbstractCssComponent = (OriginalComponent, cssName, id = null) => ({ children, ...props}) => {
	if (cssName === 'placeholder')
		props['aria-hidden'] = true;
	return (
		<OriginalComponent
			{...props}
			innerProps={id ? {id} : props.innerProps}
			className={`neo-dropdown-${cssName}`}
		>
			{children}
		</OriginalComponent>
	);
}

const CustomInput = ({ describedby, idLabel, idOption, searchable, idMenu, autoComplete, ...props}) => {
	if (!searchable)
		props.readOnly = true;

	if (autoComplete)
		props.autoComplete = autoComplete;

	return (
		<components.Input
			{...props}
			role="combobox"
			aria-haspopup="listbox"
			aria-live="assertive"
			aria-expanded={false}
			aria-labelledby={idLabel}
			aria-describedby={describedby}
			aria-controls={idMenu}
			className={`neo-dropdown-input`}
		/>
	);
}

const CustomMenu = ({ children, customChildren, options, className, idMenu, ...props }) => {
	const hasScrollbar = React.useMemo(() =>
		props?.innerRef?.current?.firstChild?.scrollHeight > props?.innerRef?.current?.firstChild?.clientHeight,
		[props?.innerRef?.current?.firstChild]
	);

	const menuProps = React.useMemo(() => {return {
		...props.innerProps,
		 role: "listbox",
		 tabindex: -1,
		 id: idMenu,
	}}, [props, idMenu]);

	return (
		<components.Menu
			className={classnames('neo-dropdown-menu', (hasScrollbar && 'has-scrollbar'), className)}
			{...props}
			innerProps={menuProps}
		>
			{options?.length > 0 ? children : [customChildren]}
		</components.Menu>
	);
};

const CustomOption = ({ children, className, currentValue, checkSelected, ...props }) => {
	const isSelected = React.useMemo(() => checkSelected(props.value, currentValue), [currentValue, checkSelected, props.value]);
	const optionProps = { ...props.innerProps, tabindex: 0, role: "option" };
	if (isSelected)
		optionProps['aria-selected'] = true;

	return (
		<components.Option
			{...props}
			innerProps={optionProps}
			className={classnames('neo-dropdown-option', className, (props.isFocused && 'focus'), (isSelected && 'selected'))}
		>
			{children}{isSelected && <Check className="neo-dropdown-check" />}
		</components.Option>
   );
};

const CustomControl = ({ children, icon, ...props }) => {
	const [clicked, setClicked] = React.useState(false);

	// differentiate between focus using click or tab
	const customInnerProps = React.useMemo(() => {
		const iprops = props.innerProps;
		iprops.onClick = () => {setClicked(true)};
		iprops.onBlur = () => {setClicked(false)};
		return iprops;
	}, [props]);

	return (
		<components.Control
			{...props}
			innerProps={customInnerProps}
			className={classnames('neo-dropdown-control', (props.menuIsOpen && 'open'), (props.isFocused && 'focus'), (clicked && 'clicked'))}
		>
			{icon && <span className='neo-dropdown-icon'>{icon}</span>}{children}
		</components.Control>
   );
};

const dropdownStyle = {
	// we override the base css with the one from the file
	container: () => {},
	control: () => {},
	indicatorsContainer: () => {},
	input: () => {},
	menu: () => {},
	option: () => {},
	placeholder: () => {},
	singleValue: () => {},
	valueContainer: (base, state) => ({
		...base,
		height: '40px',
		display: 'inline-flex',
		padding: 0,
	}),
};

export default function Dropdown({
	uniqueKey = null,
	icon = null,
	id = null,
	placeholder = null,
	label = null,
	description = null,
	show = false,
	required = false,
	searchable = false,
	disabled = false,
	errors = null,
	options,
	hideChevron = false,
	value = null,
	onClick = () => {},
	onChange = () => {},
	className,
	children,
	display = true,
	checkSelected = (o, v) => v && o === v,
	...rest
}) {
	const { t } = useTranslation();
	const orderedOptions = React.useMemo(() => options?.sort((a, b) => a?.order - b?.order), [options]);
	const selectedOption = React.useMemo(() => options?.find(option => option?.value === value), [value, options]);
	const dropdownRef = React.useRef(null);

	const getKey = (element = null) => `field_${uniqueKey}${element ? '_' + element : ''}`;
	const describedby = `${getKey('selected')} ${getKey('description')} ${getKey('error')}`;

	const _onChange = React.useCallback((value) => {
		onChange(value);
		setTimeout(() => dropdownRef?.current?.focus(), 10);
	}, [onChange, dropdownRef])

	if (!display) {
		return null
	}

	return (
		<div className='neo-dropdown-content' id={id}>
			{label && <label className='neo-input-label' id={getKey('label')}>{label} {required && <span aria-label={t('widget.required-field')} className='neo-label-required'>*</span>}</label>}
            {description && <p className='neo-input-description' id={getKey('description')}>{description}</p>}
			<ReactSelect
				ref={dropdownRef}
				openMenuOnClick
				closeMenuOnSelect={true}
				inputId={getKey()}
				{...rest}
				aria-live="assertive"
				className={classnames('neo-dropdown', className, (disabled && 'disabled'), (withErrors(errors) && 'error'))}
				options={orderedOptions}
				value={selectedOption || null}
				onChange={(option) => _onChange(option?.value)}
				isSearchable={true} // ReactSelect uses DummyInput and is not overwritable if not searchable
				components={{
					IndicatorSeparator: () => null,
					Control: (props) => CustomControl({ ...props, icon }),
					Menu: (props) => CustomMenu({ ...props, customChildren: children, idMenu: getKey('menu') }),
					Option: (props) => CustomOption({ ...props, currentValue: value, checkSelected }),
					Input: (props) => CustomInput({
						...props,
						autoComplete: rest.autoComplete || autocompleteDisabled(),
						searchable,
						describedby,
						idLabel: getKey('label'),
						idMenu: getKey('menu')
					}),
					ValueContainer: AbstractCssComponent(components.ValueContainer, 'value-container'),
					IndicatorsContainer: hideChevron ? () => null : AbstractCssComponent(components.IndicatorsContainer, 'indicators'),
					IndicatorContainer: AbstractCssComponent(components.IndicatorContainer, 'indicator'),
					MenuList: AbstractCssComponent(components.MenuList, 'menu-list'),
					SingleValue: AbstractCssComponent(components.SingleValue, 'single-value', getKey('selected')),
					Placeholder: AbstractCssComponent(components.Placeholder, 'placeholder'),
					...rest.components,
				}}
				isDisabled={disabled}
				placeholder={placeholder ? placeholder : t('widget.select')}
				styles={dropdownStyle}
			/>
			<InputErrors errors={errors} id={getKey('error')}/>
		</div>
	);
}		